<template>
  <div class="rd-screen">
    <report-tools>
      <span class="rd-muted">{{ $t("report_desktop.ordered_taken_note") }}</span>
    </report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!packingCompanies.length"
      icon="mdi-shopping-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <section v-for="company in packingCompanies" v-else :key="company.key">
      <div class="rd-section">
        <h2>
          {{ company.name }}
          <span v-if="company.subtitle" class="rd-section__sub">{{ company.subtitle }}</span>
        </h2>
        <span class="rd-num">{{ company.taken }} / {{ company.ordered }}</span>
      </div>
      <div class="rd-card">
        <table class="rd-tbl">
          <thead>
            <tr>
              <th scope="col" class="rd-col-num">#</th>
              <th scope="col">{{ $t("meal") }}</th>
              <th v-for="shift in visibleShifts" :key="shift.id" scope="col" class="is-right">
                {{ $t(shift.label) }}
              </th>
              <th scope="col" class="is-right">{{ $t("total") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in company.rows" :key="item.key">
              <td class="rd-num rd-muted">{{ index + 1 }}</td>
              <td class="is-name">
                {{ item.name }}
                <span v-if="item.combination" class="rd-muted rd-line">{{ item.combination }}</span>
              </td>
              <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num">
                {{ formatQuantity(item.source, shift.key) }}
              </td>
              <td class="is-right rd-num"><b>{{ formatQuantity(item.source, "total") }}</b></td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td></td>
              <td>{{ $t("total") }}</td>
              <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num">
                {{ formatQuantity(company.totals, shift.key) }}
              </td>
              <td class="is-right rd-num">{{ formatQuantity(company.totals, "total") }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  </div>
</template>

<script>
import ReportAllCompaniesTabPacking from "@/components/report/all-companies/tabs/ReportAllCompaniesTabPacking.vue";
import hasShiftSubtitle from "../../shared/hasShiftSubtitle";
import ReportState from "../ReportState.vue";
import ReportTools from "../ReportTools.vue";

/**
 * "Sve firme – zbirni izveštaj → Pakovanje" na računaru: po firmi stavke sa "naručeno / preuzeto"
 * po smeni. Nasleđuje učitavanje, štampu i Excel iz ReportAllCompaniesTabPacking.
 */
export default {
  name: "ReportDesktopAllPacking",
  extends: ReportAllCompaniesTabPacking,
  mixins: [hasShiftSubtitle],
  components: { ReportState, ReportTools },
  props: {
    /** Smene stižu kroz selectedShifts (prop starog taba); ovde samo da se ne prikažu kao atribut. */
    shifts: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    /** Smene 1–3 uvek, četvrta samo kad neka stavka ima obroke u njoj. */
    visibleShifts() {
      return this.selectedShiftColumns.filter(
        (shift) =>
          shift.id < 4 ||
          this.visibleCompanies.some((company) => company.items.some((item) => item[shift.key] > 0))
      );
    },
    packingCompanies() {
      const search = this.filters.search;
      return this.visibleCompanies
        .map((company) => {
          const companyMatches = !search || this.matches(company, ["name"], search);
          const rows = company.items
            .filter((item) => companyMatches || this.matches(item, ["name", "combination"], search))
            .map((item) => ({
              key: `${item.id}-${item.combination || ""}`,
              name: this.toLatin(item.name),
              combination: item.combination ? this.toLatin(item.combination) : "",
              source: item,
            }));
          return {
            key: company.id || company.name,
            name: company.name,
            subtitle: [company.address, company.city].filter(Boolean).join(", "),
            rows,
            totals: company.totals,
            ordered: company.totals.total,
            taken: company.totals.total_payed,
          };
        })
        .filter((company) => company.rows.length);
    },
    summary() {
      const ordered = this.packingCompanies.reduce((sum, company) => sum + company.ordered, 0);
      const taken = this.packingCompanies.reduce((sum, company) => sum + company.taken, 0);
      return { ordered, taken, percent: ordered ? Math.min(100, Math.round((taken / ordered) * 100)) : 0 };
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        {
          key: "taken",
          label: this.$t("taken"),
          value: this.summary.taken,
          of: this.summary.ordered,
          percent: this.summary.percent,
          tone: "inverse",
        },
        { key: "companies", label: this.$t("companies"), value: this.packingCompanies.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.packingCompanies.length ? kpis : []);
      },
      immediate: true,
    },
    loading(loading) {
      this.$emit("loading", loading);
    },
  },
  methods: {
    matches(row, fields, search) {
      return this.customSearch([row], fields.map((value) => ({ value })), search).length > 0;
    },
  },
};
</script>
